/* eslint-disable react/no-array-index-key */
import React from 'react';
import _merge from 'lodash/merge';
import moment from 'moment';
import { getContext } from 'common';
import Styled from './styles';

import Day, { CalendarDayProps } from './CalendarDay';

const getEmptyWeek: any = () => [...Array(7)].map(() => ({ emptyDay: true }));

type OnDaySelect = (arg: { day: Date, event: React.MouseEvent | React.KeyboardEvent }) => void;
type SetDayProps = (arg: Date) => Partial<CalendarDayProps>;
export interface CalendarDayPickerProps extends React.ComponentPropsWithRef<'div'> {
  /**
   * JavaScript Date object.
   * `CalendarDayPicker` shows the month including `visibleDate`.
   *  Use `CalendarMonthPicker` component to update this value.
   *  Read README tab for more information about `Visible date` and `Selected Date`.
   */
  visibleDate: Date;
  /**
   * JavaScript Date object.
   * The selected date.
   */
  date?: Date;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** `onDaySelect` is called when a new day is selected.
   * @param {object} event The event source of the callback.
   * @param {object} day The selected day.
   */
  onDaySelect?: OnDaySelect;
  /** `setDayProps` is used to configure the day. It receives the date object and can return all `CalendarDay` props including:
   * - inRange: boolean, Set to `true`, if the day is in range.
   * - firstInRange: boolean, Set to `true`, if the day is first in range.
   * - lastInRange: boolean, Set to `true`, if the day is last in range.
   * - day: PropTypes.node.isRequired, The day label.
   * - disabled: boolean, If `true`, the button will be disabled.
   * - rippleStyle: PropTypes.shape({}), Ripple style.
   * - buttonProps: PropTypes.shape({}), To alter the button DOM element attributes.
   * - theme: PropTypes.shape({}), To override the theme please refer to the "Readme" tab to learn more.
   */
  setDayProps?: SetDayProps;
}

/**
 * All props will be provided to the root DOM element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
*/
const CalendarDayPicker = React.forwardRef<HTMLElement, CalendarDayPickerProps>((props: CalendarDayPickerProps, ref) => getContext(({ RTL, theme }) => {
  const {
    onDaySelect,
    date,
    visibleDate,
    theme: themeProp,
    setDayProps,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme.CALENDAR.WEEKDAY, themeProp);

  const getWeeks = (visibleDay) => {
    const firstDayOfWeek = moment.localeData().firstDayOfWeek();
    const deltaDay = 7 - firstDayOfWeek;
    const currentMonth = visibleDay.month();
    const weeks = [getEmptyWeek()];
    visibleDay.date(1);
    while (visibleDay.month() === currentMonth) {
      const lastMadeWeek = weeks[weeks.length - 1];
      const weekday = ((visibleDay.day() as number) + deltaDay) % 7;
      const isMadeDayLastDayOfWeek = weekday === 6;
      const nextDayDate = (visibleDay.date() as number) + 1;
      lastMadeWeek[weekday] = moment(visibleDay);
      visibleDay.date(nextDayDate);
      if (isMadeDayLastDayOfWeek && visibleDay.month() === currentMonth) {
        weeks.push(getEmptyWeek());
      }
    }
    return weeks;
  };

  const momentVisibleDate = moment(visibleDate);
  const momentSelectedDate = date !== undefined ? moment(date) : undefined;
  const weekdays = moment.weekdaysMin(true, '-D-');
  const weeks = getWeeks(moment(momentVisibleDate));
  const handleDayClick = (event, day) => {
    onDaySelect && onDaySelect({
      event,
      day: moment(momentVisibleDate).month(day.month()).date(day.date()).toDate()
    });
  };

  const renderWeekdays = () =>
    weekdays.map(weekday => <Styled.Weekday $theme={resolvedTheme} key={weekday}>{weekday}</Styled.Weekday>);

  const renderDays = week => week.map((day, index) => {
    if (day.emptyDay) {
      return <Styled.CalendarEmptyDay key={`empty-day-${index as number}`} />;
    }
    const selected = momentSelectedDate !== undefined && momentSelectedDate.isSame(day, 'day');
    const dayText = day.format('D');
    const dayProps = _merge({}, {
      selected,
      day: day.format('D'),
      buttonProps: {
        onClick: (event) => { !selected && handleDayClick(event, day); }
      },
    }, setDayProps ? setDayProps(day.toDate()) : {});
    return <Day {...dayProps} key={dayText} />;
  });

  const renderWeeks = () => weeks.map((week, index) =>
    <Styled.Week key={index}>{renderDays(week)}</Styled.Week>);

  return (
    <Styled.CalendarDayPicker
      $rtl={RTL}
      ref={ref}
      {...otherProps}
    >
      <Styled.Weekdays>{renderWeekdays()}</Styled.Weekdays>
      {renderWeeks()}
    </Styled.CalendarDayPicker>
  );
}));

CalendarDayPicker.defaultProps = {
  date: undefined,
  theme: {},
  onDaySelect: undefined,
  setDayProps: undefined
};

CalendarDayPicker.displayName = 'CalendarDayPicker';

export default CalendarDayPicker;
